import {
  faClockRotateLeft,
  faRepeat,
  faUpRightFromSquare,
  faXmark,
} from '@fortawesome/duotone-regular-svg-icons';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import Box from '~/common/components/Box';
import Tab from '~/common/components/Tab/Tab';
import TabPanel from '~/common/components/Tab/TabPanel';
import Tabs from '~/common/components/Tab/Tabs';
import Text from '~/common/components/Texts/Text';
import { AUS_METHOD } from '~/common/constants';
import useUrl from '~/common/hooks/useUrl';
import Button from '~/common/mui/Buttons/MUIButton/MUIButton';
import IconButton from '~/common/mui/Buttons/MUIButton/MUIIconButton';
import ToggleButtonGroup from '~/common/mui/Buttons/MUIButtonGroup/ToggleButtonGroup';
import Divider from '~/common/mui/Divider';
import { hexToRgba } from '~/common/util/format';
import { useModalState } from '~/modals/modalStore';
import ErrorBoundary from '~/pages/Home/ErrorBoundary/ErrorBoundary';

import palette from '../../theme/mui/palette';
import AUSHistory from './AUSHistory';
import RunAUS from './RunAUS';
import { useAUSDetailsQuery } from './hooks/useAUSDetailsQuery';
import useAUSStore from './useAUSStore';

type AUSDialogProps = Readonly<{
  isOpen: boolean;
  onClose: () => void;
  dialogPosition?: Readonly<{
    top: number;
    left: number;
  }> | null;
}>;

// numeronym for Accessibility A11y
type A11yProps = {
  id: string;
  'aria-controls': string;
};

function a11yProps(index: number): A11yProps {
  return {
    id: `borrower-tab-${index}`,
    'aria-controls': `borrower-tabpanel-${index}`,
  };
}

const ausTabs = [
  {
    icon: (
      <FontAwesomeIcon
        icon={faRepeat}
        style={{ fontSize: '1.5rem', marginBottom: 8 }}
      />
    ),
    label: 'Run AUS',
    title: 'Run AUS',
    headerType: 'simple',
    itemNumber: 0,
    content: <RunAUS />,
  },
  {
    icon: (
      <FontAwesomeIcon
        icon={faClockRotateLeft}
        style={{ fontSize: '1.5rem', marginBottom: 8 }}
      />
    ),
    label: 'History',
    title: 'History',
    headerType: 'buttons',
    itemNumber: 1,
    content: <AUSHistory />,
  },
];

const options = [
  { value: 'DU', label: 'DU' },
  { value: 'LPA', label: 'LPA' },
];

export default function AUSDialogContainer() {
  const { isOpen, onClose } = useModalState('AUS');
  const { pathname } = useLocation();

  useEffect(() => {
    // Only visible on pages where the action bar is present. There is no action bar on sumary page.
    if (isOpen && pathname.includes('/summary/')) {
      onClose();
    }
  }, [pathname, isOpen, onClose]);

  return <AUSDialog isOpen={isOpen} onClose={onClose} />;
}

function AUSDialog({ isOpen, onClose }: AUSDialogProps) {
  const [tabValue, setTabValue] = useState(ausTabs[0].itemNumber);
  const activeTab = ausTabs.find((tab) => tab.itemNumber === tabValue);
  const [selectedButton, setSelectedButton] = useState(options[0].value);
  const { setHistoryType, backToRunAUS, isAUSRunSuccessful } = useAUSStore();
  const { obfuscatedLoanIdentifier } = useUrl();
  const { data: ausDetails } = useAUSDetailsQuery();

  const { obfuscatedLenderDatabaseId, obfuscatedLoanRecordId } =
    obfuscatedLoanIdentifier || {};

  const rawLastRunAUS = ausDetails?.lastAusRunDetails?.requestMethod;
  const lastRunType =
    (typeof rawLastRunAUS === 'number'
      ? AUS_METHOD[rawLastRunAUS as keyof typeof AUS_METHOD]
      : rawLastRunAUS) ?? options[0].value;

  useEffect(() => {
    setSelectedButton(lastRunType);
    setHistoryType(lastRunType === 'DU' ? 'DU' : 'LPA');
  }, [lastRunType, setHistoryType]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleToggleChange = (value: string) => {
    setSelectedButton(value);
    setHistoryType(value === 'DU' ? 'DU' : 'LPA');
  };

  return (
    <ErrorBoundary>
      <Dialog
        aria-label="AUS Dialog"
        open={isOpen}
        onClose={onClose}
        hideBackdrop
        disableEnforceFocus
        disablePortal={false}
        maxWidth={false}
        disableScrollLock // Prevents scroll jump when dialog opens
        slotProps={{
          paper: {
            style: {
              alignSelf: 'flex-end',
              width: 838,
              height: 560,
              borderRadius: '1rem',
              boxShadow: `
              0 1px 18px 0 ${hexToRgba(palette.black, 0.12)},
              0 6px 10px 0 ${hexToRgba(palette.black, 0.14)},
              0 3px 5px -1px ${hexToRgba(palette.black, 0.2)}
            `,
              pointerEvents: 'auto',
              margin: 0,
              marginBottom: '80px',
            },
          },
        }}
        sx={{
          pointerEvents: 'none',
          '& .MuiDialog-paper': { pointerEvents: 'auto' },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            padding: 0,
            minHeight: '72px',
          }}
        >
          <Text
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minWidth: '79px',
              height: '72px',
              fontWeight: 500,
              fontSize: '1rem',
              lineHeight: '1.5rem',
              color: 'blue.900',
              padding: '1.5rem 0 1rem 0',
            }}
          >
            AUS
          </Text>
          <Divider
            orientation="vertical"
            sx={{ height: '100%', opacity: 1, marginRight: '1.5rem' }}
          />
          <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
            {isAUSRunSuccessful && activeTab?.title === 'Run AUS' ? (
              <Button
                variant="text"
                startIcon={
                  <FontAwesomeIcon
                    icon={faArrowLeft}
                    style={{
                      fontSize: '1.5rem',
                      boxSizing: 'content-box',
                      maxWidth: '20px',
                      maxHeight: '20px',
                      minWidth: '20px',
                      minHeight: '20px',
                      marginRight: '4px',
                    }}
                  />
                }
                sx={{ padding: 'unset' }}
                onClick={backToRunAUS}
              >
                Run AUS
              </Button>
            ) : (
              <Text
                as="h2"
                sx={{
                  fontWeight: 500,
                  color: 'blue.900',
                  fontSize: '1.5rem',
                }}
              >
                {activeTab?.title}
              </Text>
            )}
            {activeTab?.headerType === 'buttons' && (
              <Box
                component="section"
                sx={{
                  display: 'flex',
                  flexGrow: 1,
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginRight: '4rem',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginLeft: '1rem',
                  }}
                >
                  <ToggleButtonGroup
                    id="aus-history-toggle-group"
                    options={options}
                    value={selectedButton}
                    onChange={handleToggleChange}
                  />
                  <Text
                    sx={{
                      marginLeft: '0.5rem',
                      color: 'black',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                    }}
                  >
                    {selectedButton === 'DU'
                      ? 'Desktop Underwriter'
                      : 'Loan Product Advisor'}
                  </Text>
                </Box>
                <Button
                  variant="outlined"
                  endIcon={
                    <FontAwesomeIcon
                      color={palette.blue[500]}
                      icon={faUpRightFromSquare}
                    />
                  }
                  onClick={() => {
                    window.open(
                      `/${obfuscatedLenderDatabaseId}/${obfuscatedLoanRecordId}/aus/history?showBothTables=1`,
                      '_blank',
                      `left=0,top=0,width=${window.screen.availWidth},height=${window.screen.availHeight}`,
                    );
                  }}
                >
                  Full Screen
                </Button>
              </Box>
            )}
            <IconButton
              aria-label="Close AUS Dialog"
              sx={{
                position: 'absolute',
                right: '0.7rem',
                padding: '1rem',
                minWidth: '2.5rem',
                minHeight: '2.5rem',
                width: 'auto',
                height: 'auto',
              }}
              onClick={onClose}
            >
              <FontAwesomeIcon
                style={{ fontSize: '1rem' }}
                color={palette.black}
                icon={faXmark}
              />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ padding: 0, display: 'flex', height: '100%' }}>
          <Tabs
            aria-label="AUS Tabs"
            value={tabValue}
            onChange={handleTabChange}
            orientation="vertical"
            sx={{
              borderRight: 1,
              borderColor: 'divider',
              minWidth: '81px',
            }}
          >
            {ausTabs.map((tab, idx) => (
              <Tab
                key={tab.label}
                value={tab.itemNumber}
                sx={{
                  minWidth: '47px',
                  padding: '8px 16px',
                  marginBottom: '8px',
                }}
                {...a11yProps(idx)}
                label={
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      fontSize: '0.75rem',
                    }}
                  >
                    <span>{tab.icon}</span>
                    <div>{tab.label}</div>
                  </Box>
                }
              />
            ))}
          </Tabs>
          {ausTabs.map((tab, idx) => (
            <TabPanel
              key={tab.label}
              name="ausPanels"
              index={idx}
              value={tabValue}
              padding="0 1.5rem 1.5rem 1.5rem"
            >
              {tab.content}
            </TabPanel>
          ))}
        </DialogContent>
      </Dialog>
    </ErrorBoundary>
  );
}
